import { FormattedMessage, InvoiceExpandedSummary, InvoiceSummaryRawData, useMelioIntl } from '@melio/ar-domain';
import { Group, SectionBanner, Text, useFormSubmissionController } from '@melio/penny';
import { useAnalyticsView } from '@melio/platform-analytics';
import { forwardRef } from '@melio/platform-utils';

import { InvoiceFormLayout } from '../../../layouts';
import { InvoiceForm, InvoiceFormFooter, InvoiceFormProps } from '../components';
import { DiscountProps, EditInvoiceSubmitTarget, ServiceChargesProps, TaxRateProps } from '../types';
import { InvoiceFormState, InvoiceFormValues } from '../utils';

export type EditInvoiceScreenProps = {
  onClose: VoidFunction;
  onSubmit: (
    data: InvoiceFormValues,
    event?: React.MouseEvent<HTMLButtonElement>,
    target?: EditInvoiceSubmitTarget
  ) => unknown;
  taxRate?: TaxRateProps;
  discount?: DiscountProps;
  serviceCharges?: ServiceChargesProps;
  isSaving?: EditInvoiceSubmitTarget;
  isDisabled?: boolean;
  isLoading?: boolean;
  isLoadingSummary?: boolean;
  defaultValues?: InvoiceFormProps['defaultValues'];
  error?: PlatformError;
  invoiceSummary?: InvoiceSummaryRawData;
  onLineItemsChange?: (lineItems: InvoiceFormValues['lineItems']) => unknown;
  onFormStateChange?: (state: InvoiceFormState) => void;
  invoice?: InvoiceExpandedSummary;
};

export const EditInvoiceScreen = forwardRef<EditInvoiceScreenProps>(
  (
    {
      defaultValues,
      isSaving,
      isLoading,
      isLoadingSummary,
      discount = { onEdit: undefined, id: undefined, onRemove: undefined },
      taxRate = { onEdit: undefined, id: undefined },
      serviceCharges = { onAdd: undefined, onRemove: undefined },
      onSubmit,
      error,
      invoiceSummary,
      onLineItemsChange,
      onClose,
      onFormStateChange,
      invoice,
      ...props
    },
    ref
  ) => {
    const { onSubmissionStateChange, handleSubmit, submitButtonProps } =
      useFormSubmissionController<InvoiceFormValues>();

    const { formatMessage, formatDate } = useMelioIntl();

    useAnalyticsView('Invoice', true, true, {
      ProductName: 'ar',
      PageName: 'edit-invoice',
      Flow: 'edit-invoice',
      Intent: 'edit-invoice',
    });

    return (
      <InvoiceFormLayout
        backgroundColor="white"
        variant="10Columns"
        isLoading={isLoading}
        onClose={onClose}
        footer={
          <InvoiceFormFooter
            isDisabled={submitButtonProps?.isDisabled || isLoadingSummary || isLoading}
            isSaving={!!isSaving}
            primaryButtonProps={{
              label: formatMessage(
                invoice?.issuedAt
                  ? `ar.invoiceLifecycle.activities.editInvoice.buttons.saveAndContinue.issued.label`
                  : `ar.invoiceLifecycle.activities.editInvoice.buttons.saveAndContinue.draft.label`
              ),
              isLoading: isSaving === 'save-and-continue',
              onClick: handleSubmit?.('save-and-continue'),
            }}
            secondaryButtonProps={{
              label: formatMessage(
                invoice?.issuedAt
                  ? `ar.invoiceLifecycle.activities.editInvoice.buttons.saveAndClose.issued.label`
                  : `ar.invoiceLifecycle.activities.editInvoice.buttons.saveAndClose.draft.label`
              ),
              isLoading: isSaving === 'save-and-close',
              onClick: handleSubmit?.('save-and-close'),
            }}
          />
        }
        data-component={EditInvoiceScreen.displayName}
        data-testid="create-invoice-screen"
        {...props}
        ref={ref}
      >
        <Group variant="vertical" spacing="xl" data-testid="invoice-screen-content">
          <Text textStyle="heading1Semi">
            <FormattedMessage id="ar.invoiceLifecycle.activities.editInvoice.screens.form.header" />
          </Text>
          {invoice?.issuedAt && (
            <SectionBanner
              variant="info"
              title={formatMessage('ar.invoiceLifecycle.activities.editInvoice.draft.sectionBanner.title', {
                issuedAt: formatDate(invoice.issuedAt, { dateStyle: 'medium' }),
              })}
              description={formatMessage('ar.invoiceLifecycle.activities.editInvoice.draft.sectionBanner.description')}
            />
          )}
          <InvoiceForm
            mode="edit"
            invoiceSummary={invoiceSummary}
            discount={discount}
            taxRate={taxRate}
            serviceCharges={serviceCharges}
            onSubmissionStateChange={onSubmissionStateChange}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
            isSaving={!!isSaving}
            isLoadingSummary={isLoadingSummary}
            onLineItemsChange={onLineItemsChange}
            onFormStateChange={onFormStateChange}
            error={error}
          />
        </Group>
      </InvoiceFormLayout>
    );
  }
);
EditInvoiceScreen.displayName = 'EditInvoiceScreen';
